import React, { useEffect, useMemo, useState } from 'react';
import { Chevron } from '../components/Glyph';
import { dayShort, clock, dayShortAndClock, siteToday, siteDayStart, nextDay } from '../lib/when';
import { supabase } from '../lib/supabase';
import { fetchAll } from '../lib/save';
import PageBar, { SearchInput } from '../components/PageBar';
import ListGrid from '../components/ListGrid';
import Dialog from '../components/Dialog';
import { IconDoc, IconFilter } from '../components/dialogIcons';
import useMediaQuery from '../hooks/useMediaQuery';
import { useToast } from '../components/ToastContext';
import { downloadCsv, DRAW_CAP } from './reports/reportData';
import { ACTION_WORDS, KINDS, actionWords, auditFigures, kindCounts, kindLabel } from './reports/auditWords';

/*
  The Audit Log: who did what, and when.

  **Rebuilt 6 September 2026 to Group F's approved drawing.** It was already the
  shape the other three reports were rebuilt into, which is why the build order
  named it the model, and it needed the shell and the grid rather than a rebuild
  of its idea.

  Four things changed and each was a fault rather than a preference:

  - **Every action gets a plain sentence.** Three of the eighteen events in the
    test site printed as `SITE_CREATED`, `SITE_DEACTIVATED` and
    `SITE_SETTINGS_UPDATED`, in capitals with underscores. See `auditWords.js`.
  - **The kind filter says how many events each kind holds**, so the three kinds
    that can never match anything say 0 rather than looking like a filter that
    found nothing.
  - **The totals strip carries a red 0 beside Counting.** Nothing about counting
    is ever recorded, and that is the finding, on the face of the screen.
  - **The rows are a grid.** Rule 15. This screen drew an HTML table.

  **And the dialog that opens one event could not open.** It used `Dialog` and
  `IconDoc` and this file imported neither, so pressing Open threw and the
  screen went blank. Found on 6 September 2026 while rebuilding it, by reading
  the imports rather than by anything on a picture: nothing on the list looked
  wrong until you pressed something.
*/

/*
  How far back the screen reads in one go.

  A request brings back about a thousand rows and says nothing about the rest,
  so this pages with `fetchAll`. It stops at a ceiling because an audit log has
  no natural end, and **when it stops it says so on the screen**: a report that
  quietly holds the most recent slice while printing a confident total is the
  exact fault Group F found in the Count History export.
*/
export const READ_CEILING = 5000;

export default function AdminAuditLog() {
  const toast = useToast();
  const wide = useMediaQuery('(min-width: 701px)');

  const [events, setEvents] = useState(null);
  const [people, setPeople] = useState([]);
  const [pickedKinds, setPickedKinds]   = useState([]);
  const [pickedPeople, setPickedPeople] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);       // which filter dialog is showing
  const [event, setEvent] = useState(null);     // which event is opened
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    supabase.from('profiles').select('id, full_name').order('full_name')
      .then(({ data }) => setPeople(data || []));
  }, []);

  useEffect(() => {
    let live = true;
    setEvents(null);
    (async () => {
      const { data, error } = await fetchAll(() => {
        let q = supabase.from('audit_logs').select('*').order('created_at', { ascending: false });
        // **The site's day, not Greenwich's** (the audit after the rebrand,
        // issue 23, 18 September 2026). A time with no zone on it is read by
        // the database as Greenwich, so on a Texas site "from the 18th" began
        // at 7pm on the 17th and "to the 18th" stopped at 7pm on the 18th.
        if (from) q = q.gte('created_at', siteDayStart(from));
        if (to)   q = q.lt('created_at', siteDayStart(nextDay(to)));
        return q;
      }, { max: READ_CEILING });
      if (!live) return;
      if (error) { toast.error('Could not read the log: ' + error.message); setEvents([]); return; }
      setEvents(data || []);
    })();
    return () => { live = false; };
    // eslint-disable-next-line
  }, [from, to]);

  const matched = useMemo(() => {
    if (events === null) return [];
    const q = search.trim().toLowerCase();
    return events.filter(e => {
      if (pickedKinds.length && !pickedKinds.includes(e.category)) return false;
      if (pickedPeople.length && !pickedPeople.includes(e.user_id)) return false;
      if (!q) return true;
      return [actionWords(e.action), e.user_name, e.target_name, e.action]
        .some(v => (v || '').toLowerCase().includes(q));
    });
  }, [events, pickedKinds, pickedPeople, search]);

  const figures = useMemo(() => auditFigures(matched), [matched]);
  const counts  = useMemo(() => kindCounts(events || []), [events]);
  const ceilingHit = events !== null && events.length >= READ_CEILING;

  function runExport() {
    if (!matched.length) return;
    const header = ['When', 'Who', 'Role', 'Kind', 'What happened', 'What it was about', 'Details'];
    const rows = matched.map(e => [
      dayShortAndClock(e.created_at),
      e.user_name || '',
      e.user_role || '',
      kindLabel(e.category),
      actionWords(e.action),
      e.target_name || '',
      e.details ? JSON.stringify(e.details) : '',
    ]);
    downloadCsv([header, ...rows], 'OnHand_AuditLog_' + siteToday());
    toast.success('Exported ' + matched.length.toLocaleString() + ' events');
  }

  const filterCount = pickedKinds.length + pickedPeople.length + (from ? 1 : 0) + (to ? 1 : 0);
  const shown = matched.slice(0, DRAW_CAP);
  const left  = matched.length - shown.length;

  return (
    <>
      <PageBar
        title="Audit Log"
        subtitle="Who did what, and when"
        controls={wide ? (
          <>
            <SearchInput value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search a person or a thing" />
            <button className="rp-chip" onClick={() => setOpen('kinds')}>
              All kinds{pickedKinds.length > 0 && <span className="rp-chip-n">- {pickedKinds.length}</span>}
              <Chevron size={11} className="rp-chip-c" />
            </button>
            <button className="rp-chip" onClick={() => setOpen('people')}>
              All people{pickedPeople.length > 0 && <span className="rp-chip-n">- {pickedPeople.length}</span>}
              <Chevron size={11} className="rp-chip-c" />
            </button>
            <button className="rp-chip" onClick={() => setOpen('dates')}>
              Any date{(from || to) && <span className="rp-chip-n">- set</span>}
              <Chevron size={11} className="rp-chip-c" />
            </button>
          </>
        ) : (
          <>
            <button className="rp-icon" aria-label="Search" onClick={() => setSearchOpen(o => !o)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.8" />
                <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <button className="rp-chip" onClick={() => setOpen('all')}>
              Filters{filterCount > 0 && <span className="rp-chip-n">- {filterCount}</span>}
              <Chevron size={11} className="rp-chip-c" />
            </button>
          </>
        )}
        meta={events === null ? 'Reading...' : figures.events.toLocaleString() + ' events'}
        actions={
          <button className="tb-btn tb-btn-primary" onClick={runExport} disabled={!matched.length}>
            Export {figures.events.toLocaleString()} event{figures.events === 1 ? '' : 's'}
          </button>
        }
      />

      {!wide && searchOpen && (
        <div style={{ marginBottom: 12 }}>
          <SearchInput value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search a person or a thing" style={{ width: '100%' }} autoFocus />
        </div>
      )}

      <div className="rpsum">
        <Figure n={figures.events} label="Events" />
        <Figure n={figures.people} label="People" />
        <Figure n={figures.auth} label="Signing in" />
        <Figure n={figures.sites} label="Sites" />
        <Figure n={figures.counting} label="Counting" red={figures.counting === 0} />
      </div>

      {ceilingHit && (
        <div style={{ marginBottom: 14, borderRadius: 12, padding: '13px 15px', fontSize: 13, lineHeight: 1.55, background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}>
          This is the most recent {READ_CEILING.toLocaleString()} events. There are older ones.
          Pick a From date to read further back.
        </div>
      )}

      {events === null ? (
        <div className="rplist" style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          Reading the log...
        </div>
      ) : matched.length === 0 ? (
        <div className="rpnone">
          <div className="rpnone-t">Nothing matched these filters</div>
          <div className="rpnone-s">
            {events.length === 0
              ? 'Nothing has been recorded in this site yet.'
              : 'The log holds ' + events.length.toLocaleString() + ' events and none of them match. '
                + 'Exporting now would give you a file with its headings and nothing underneath.'}
          </div>
          {filterCount > 0 && (
            <div className="rpnone-a">
              <button className="btn btn-quiet" onClick={() => { setPickedKinds([]); setPickedPeople([]); setFrom(''); setTo(''); setSearch(''); }}>
                Clear every filter
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="rplist">
          <ListGrid
            layoutKey="audit_log"
            layoutTitle="The Audit Log's columns"
            columns={[
              { label: 'When',              width: '190px' },
              { label: 'Who',               width: '150px' },
              { label: 'What happened',     width: 'minmax(0,1.2fr)' },
              { label: 'What it was about', width: 'minmax(0,1.4fr)' },
              { label: '',                  width: '92px', align: 'right' },
            ]}
            rows={shown}
            keyOf={e => e.id}
            rowClass={e => 'state-counted'}
            cells={e => [
              <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                <b style={{ fontWeight: 800 }}>{dayShort(e.created_at)}</b>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>{clock(e.created_at)}</span>
              </span>,
              <span style={{ fontSize: 13, fontWeight: 700 }}>{e.user_name || '-'}</span>,
              <span style={{ fontSize: 13 }}>{actionWords(e.action)}</span>,
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{e.target_name || '-'}</span>,
              <button className="btn btn-quiet" style={{ padding: '6px 14px' }} onClick={() => setEvent(e)}>Open</button>,
            ]}
            /* The card Group F drew: what happened, who and what it was
               about, then when, with Open beside them rather than under them. */
            stack={e => (
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 700 }}>{actionWords(e.action)}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>
                    {e.user_name || '-'}{e.target_name ? '  -  ' + e.target_name : ''}
                  </span>
                  <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>
                    {dayShort(e.created_at)}, {clock(e.created_at)}
                  </span>
                </span>
                <button className="btn btn-quiet" style={{ minHeight: 44, flexShrink: 0 }} onClick={() => setEvent(e)}>Open</button>
              </span>
            )}
          />
          {left > 0 && (
            <div className="rpmore">and {left.toLocaleString()} more event{left === 1 ? '' : 's'}</div>
          )}
        </div>
      )}

      {open && (
        <AuditFilters
          which={open}
          onClose={() => setOpen(null)}
          people={people} counts={counts}
          pickedKinds={pickedKinds} setPickedKinds={setPickedKinds}
          pickedPeople={pickedPeople} setPickedPeople={setPickedPeople}
          from={from} setFrom={setFrom} to={to} setTo={setTo}
          rowCount={figures.events}
        />
      )}

      {event && <EventDialog event={event} onClose={() => setEvent(null)} />}
    </>
  );
}

function Figure({ n, label, red }) {
  return (
    <span className="rpsum-c">
      <b className={'rpsum-n' + (red ? ' red' : '')}>{n.toLocaleString()}</b>
      <span className="rpsum-l">{label}</span>
    </span>
  );
}

function AuditFilters({ which, onClose, people, counts, pickedKinds, setPickedKinds,
  pickedPeople, setPickedPeople, from, setFrom, to, setTo, rowCount }) {
  const all = which === 'all';
  const title = all ? 'Filters'
    : which === 'kinds' ? 'Which kinds?'
    : which === 'people' ? 'Which people?' : 'Which dates?';

  function clear() {
    if (all || which === 'kinds')  setPickedKinds([]);
    if (all || which === 'people') setPickedPeople([]);
    if (all || which === 'dates')  { setFrom(''); setTo(''); }
  }

  return (
    <Dialog
      kind="info" icon={<IconFilter />} title={title}
      says="Leave everything unticked to see them all."
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={clear}>Clear</button>
          <button className="btn btn-primary" onClick={onClose}>
            Show {rowCount.toLocaleString()} event{rowCount === 1 ? '' : 's'}
          </button>
        </>
      }
    >
      {(all || which === 'kinds') && (
        <div style={{ marginBottom: 14 }}>
          {all && <SectionTitle>Kinds</SectionTitle>}
          <div className="rp-ticks">
            {KINDS.map(k => (
              <label key={k.key} className={'rp-tick' + (pickedKinds.includes(k.key) ? ' on' : '')}>
                <input type="checkbox" checked={pickedKinds.includes(k.key)}
                  onChange={() => setPickedKinds(p => p.includes(k.key) ? p.filter(x => x !== k.key) : [...p, k.key])} />
                <span className="rp-tick-l">{k.label}</span>
                {/* A kind that can never match says 0 rather than looking like a
                    filter that simply found nothing today. */}
                <span className="rp-tick-s" style={counts[k.key] === 0 ? { color: 'var(--text-secondary)' } : undefined}>
                  {(counts[k.key] || 0).toLocaleString()}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      {(all || which === 'people') && (
        <div style={{ marginBottom: 14 }}>
          {all && <SectionTitle>People</SectionTitle>}
          <div className="rp-ticks" style={{ maxHeight: 240, overflowY: 'auto' }}>
            {people.map(p => (
              <label key={p.id} className={'rp-tick' + (pickedPeople.includes(p.id) ? ' on' : '')}>
                <input type="checkbox" checked={pickedPeople.includes(p.id)}
                  onChange={() => setPickedPeople(x => x.includes(p.id) ? x.filter(y => y !== p.id) : [...x, p.id])} />
                <span className="rp-tick-l">{p.full_name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {(all || which === 'dates') && (
        <div>
          {all && <SectionTitle>Dates</SectionTitle>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
            <label>
              <span className="form-lbl" style={{ marginTop: 0 }}>From</span>
              <input type="date" className="fld" value={from} onChange={e => setFrom(e.target.value)} />
            </label>
            <label>
              <span className="form-lbl" style={{ marginTop: 0 }}>To</span>
              <input type="date" className="fld" value={to} onChange={e => setTo(e.target.value)} />
            </label>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '0 0 6px' }}>
      {children}
    </div>
  );
}

/*
  One event, opened. Rule 34: the kind reads from a tinted square, and **a
  dialog with nothing to decide gets one button.** The old one painted its own
  blue header and could not open at all, because this file used `Dialog`
  without importing it.
*/
function EventDialog({ event, onClose }) {
  const details = event.details || {};
  const entries = Object.entries(details);
  return (
    <Dialog
      kind="info" icon={<IconDoc />}
      title={actionWords(event.action)}
      record={dayShortAndClock(event.created_at)}
      onClose={onClose}
      footer={<button className="btn btn-primary" onClick={onClose}>Close</button>}
    >
      <div className="hub-facts">
        <Fact label="Who" value={event.user_name || '-'} />
        <Fact label="Their job level" value={event.user_role || '-'} />
        <Fact label="Kind" value={kindLabel(event.category)} />
        <Fact label="What it was about" value={event.target_name || '-'} />
      </div>
      <div style={{ marginTop: 16 }}>
        <SectionTitle>What was recorded</SectionTitle>
        {entries.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Nothing recorded</div>
        ) : entries.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, fontSize: 13, marginTop: 6 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{k.replace(/_/g, ' ')}</span>
            <span style={{ fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>
              {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '-')}
            </span>
          </div>
        ))}
      </div>
      {/* The words on the row come from a list, and a row with no entry falls
          back to the action's own name. Showing it here means a missing one can
          be found rather than guessed at. */}
      {!ACTION_WORDS[event.action] && (
        <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-secondary)' }}>
          Recorded as {event.action}
        </div>
      )}
    </Dialog>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{value}</div>
    </div>
  );
}
